/**
 * Cliente WebSocket de la SALA VIVA (hito M1 · "la sala viva").
 *
 * Un único canal por sesión, proxied por Apache en /ws:
 *   GET (upgrade) /ws/sesiones/{sesionId}?token=<access_jwt>&desde=<event_id?>
 *
 * Responsabilidades de ESTA capa (transporte puro, sin React):
 *   · Conectar con el JWT en el query 'token' (mismo secreto que el Bearer).
 *   · REPLAY: al (re)conectar reenvía el último event_id conocido en 'desde'
 *     para no perder eventos; el servidor hace XRANGE (desde, +] y luego
 *     emite {type:"replay_fin", last_event_id}.
 *   · RECONEXIÓN con backoff exponencial + full jitter (redes de taller
 *     hostiles). Reintenta indefinidamente; nunca abandona la sesión.
 *   · HEARTBEAT keepalive cada ~20 s ({type:"heartbeat"}); el servidor cierra
 *     conexiones inertes.
 *   · SEÑAL DE ESTADO: conectando -> en_vivo; tras varios fallos -> degradado
 *     (la UI cae al polling), pero SIGUE reintentando para recuperar el vivo.
 *
 * El contrato de eventos servidor->cliente es EXACTO y compartido con el
 * backend; ver {@link SalaEvento}. La lógica de caché/estado vive en
 * hooks/useSalaViva.ts, no acá.
 */
import type { Aporte, Agregado, ActividadEstado } from './sesion';

/* ============================ Estado de conexión ======================= */

/** Señal de salud del canal, tal como la consume la UI. */
export type EstadoConexion = 'conectando' | 'en_vivo' | 'degradado';

/* ============================ Presencia (C7) =========================== */

/** Presencia SIEMPRE agregada por grupo, jamás por persona (C7). */
export interface PresenciaGrupo {
  grupo: string;
  n: number;
}

export interface Presencia {
  conectados_total: number;
  por_grupo: PresenciaGrupo[];
}

/* ============================ Eventos del contrato ===================== */

/** Campos comunes a todo mensaje servidor->cliente. */
interface EvComun {
  event_id?: string;
  server_now?: string;
}

export interface EvAporteNuevo {
  type: 'aporte_nuevo';
  actividad_id: string;
  aporte: Aporte;
}

export interface EvActividadEstado {
  type: 'actividad_estado';
  actividad_id: string;
  estado: ActividadEstado;
  abierta_at: string | null;
  cerrada_at: string | null;
  duracion_min: number | null;
  pausa_acumulado_s: number;
}

export interface EvAgregado {
  type: 'agregado';
  actividad_id: string;
  agregado: Agregado;
}

export interface EvPresencia {
  type: 'presencia';
  conectados_total: number;
  por_grupo: PresenciaGrupo[];
}

export interface EvCierre {
  type: 'cierre';
  actividad_id: string;
  artefacto_id: string;
}

export interface EvReplayFin {
  type: 'replay_fin';
  last_event_id: string;
}

/**
 * M2 · evidencia rica. El worker del host terminó de transcribir un aporte de
 * audio: el texto ya es citable en la Cabina y visible en el Bolsillo de quien
 * lo levantó. Se adjunta al aporte (su ⓘ apunta al aporte).
 */
export interface EvTranscripcionLista {
  type: 'transcripcion_lista';
  aporte_id: string;
  texto: string;
  confianza: number;
}

/**
 * M2 · moderación. El facilitador ocultó o restauró un aporte desde la Cabina:
 * la Pantalla lo excluye (o lo devuelve) en vivo. El aporte nunca se borra (C1).
 */
export interface EvModeracion {
  type: 'moderacion';
  actividad_id: string;
  aporte_id: string;
  oculto: boolean;
}

/**
 * ADR-0005 §2 · ronda dirigida. El facilitador fijó (o levantó, null) la
 * sección activa: el Bolsillo cambia de paso en vivo.
 */
export interface EvRonda {
  type: 'ronda';
  actividad_id: string;
  seccion: string | null;
}

/**
 * Switch de proyección: el facilitador fijó (o levantó, null = automático) el
 * modo de la Pantalla — 'explicar' proyecta la consigna completa sin aportes,
 * 'tablero' proyecta el formato con las participaciones.
 */
export interface EvProyeccion {
  type: 'proyeccion';
  actividad_id: string;
  modo: 'explicar' | 'tablero' | null;
}

/**
 * Micrófono de sala: el worker terminó de diarizar una grabación abierta y
 * ya sembró un aporte citable por intervención. La Cabina refresca su lista.
 */
export interface EvSalaDiarizada {
  type: 'sala_diarizada';
  actividad_id: string;
  aporte_id: string;
  segmentos: number;
  hablantes: number;
}

/** Unión discriminada de todo lo que puede llegar por el canal. */
export type SalaEvento = EvComun &
  (
    | EvAporteNuevo
    | EvSalaDiarizada
    | EvActividadEstado
    | EvAgregado
    | EvPresencia
    | EvCierre
    | EvReplayFin
    | EvTranscripcionLista
    | EvModeracion
    | EvRonda
    | EvProyeccion
  );

/* ============================ Parámetros del socket ==================== */

export interface SalaVivaOpts {
  sesionId: string;
  /** Proveedor del access JWT vigente (se relee en cada intento). */
  getToken: () => string | null;
  /**
   * Renueva el access token contra la cookie httpOnly. El token del socket
   * viaja en el query del handshake: cuando vence (15 min), NADIE lo refresca
   * por él y el socket se queda reintentando con un JWT muerto para siempre.
   * Sin esto, quien bloquea el teléfono un minuto pierde el vivo para el resto
   * de la jornada.
   */
  refrescarToken?: () => Promise<boolean>;
  /** event_id inicial desde el que pedir replay (opcional). */
  desde?: string | null;
  /** Un evento del contrato listo para aplicar a la caché. */
  onEvento: (ev: SalaEvento) => void;
  /** Cambios de la señal de conexión (para pintar el badge y el modo degradado). */
  onEstado: (estado: EstadoConexion) => void;
  /** Se emitió replay_fin: el replay terminó, ya estamos al día. */
  onReplayFin?: (lastEventId: string) => void;
}

/* ============================ Constantes de reconexión ================= */

/** A partir de este nº de intentos fallidos seguidos, la UI muestra "degradado". */
const UMBRAL_DEGRADADO = 4;
/** Base del backoff exponencial. */
const BASE_MS = 600;
/** Techo del backoff (el jitter reparte entre 0 y este valor). */
const MAX_MS = 15_000;
/**
 * Techo cuando el servidor rechaza por autorización y renovar el token NO lo
 * arregló: el problema no es la credencial (típicamente, no se es miembro del
 * proceso de esa sesión). Seguimos reintentando —el facilitador puede darnos
 * la membresía en cualquier momento— pero sin martillar: con el techo normal
 * son ~2 000 handshakes por jornada, cada uno secuestrando un worker de Apache.
 */
const MAX_AUTH_MS = 90_000;
/** Cadencia del keepalive. */
const HEARTBEAT_MS = 20_000;
/** Espera corta tras renovar el token: el rechazo ya está explicado, no hay que penalizar. */
const REINTENTO_TOKEN_NUEVO_MS = 400;
/** No pedir un token nuevo más seguido que esto (una caída de red no es un 401). */
const REFRESH_MIN_MS = 30_000;
/** Cierre de aplicación con el que el servidor rechaza token inválido / no miembro. */
const CIERRE_AUTH = 4401;

/** Construye la URL ws(s) absoluta respetando el esquema de la página. */
function construirUrl(sesionId: string, token: string | null, desde: string | null): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (desde) params.set('desde', desde);
  const qs = params.toString();
  return `${proto}//${window.location.host}/ws/sesiones/${encodeURIComponent(sesionId)}${
    qs ? `?${qs}` : ''
  }`;
}

/* ============================ Socket ================================== */

/**
 * Conexión gestionada a la sala viva. Instanciar una por sesión montada;
 * llamar {@link conectar} al montar y {@link cerrar} al desmontar.
 */
export class SalaVivaSocket {
  private ws: WebSocket | null = null;
  private intentos = 0;
  private lastEventId: string | null;
  private cerradoPorCliente = false;
  private hbTimer: number | null = null;
  private reconnTimer: number | null = null;
  /** Token con el que se intentó la última conexión (para saber si cambió). */
  private tokenProbado: string | null = null;
  /** Epoch ms del último intento de refresh (throttle). */
  private ultimoRefresh = 0;
  /**
   * Ya se gastó el reintento rápido de este ciclo. Un refresh SIEMPRE devuelve
   * un token distinto, también cuando el rechazo no era por el token (no ser
   * miembro del proceso, p. ej.): sin esta marca, ese caso se reintentaría
   * rápido una y otra vez. Se libera cuando el socket abre de verdad.
   */
  private reintentoRapidoGastado = false;
  /** Listener de visibilidad, registrado mientras el socket está vivo. */
  private onVisibilidad: (() => void) | null = null;

  constructor(private readonly opts: SalaVivaOpts) {
    this.lastEventId = opts.desde ?? null;
  }

  /** Último event_id aplicado (para replay tras reconexión). */
  get ultimoEventId(): string | null {
    return this.lastEventId;
  }

  conectar(): void {
    this.cerradoPorCliente = false;
    this.escucharVisibilidad();
    this.abrir();
  }

  cerrar(): void {
    this.cerradoPorCliente = true;
    this.limpiarTimers();
    this.dejarDeEscucharVisibilidad();
    if (this.ws) {
      // Desarmamos handlers para que el close intencional no dispare reconexión.
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      try {
        this.ws.close();
      } catch {
        /* noop */
      }
      this.ws = null;
    }
  }

  private abrir(): void {
    if (this.intentos === 0) this.opts.onEstado('conectando');

    // ¿Llegó a completarse el handshake? Un rechazo de autorización cierra
    // ANTES de abrir, así que `abrio === false` en el close significa
    // "el servidor no me dejó entrar" (o la red no llegó).
    let abrio = false;

    let ws: WebSocket;
    try {
      const token = this.opts.getToken();
      this.tokenProbado = token;
      ws = new WebSocket(construirUrl(this.opts.sesionId, token, this.lastEventId));
    } catch {
      void this.programarReconexion(true);
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      abrio = true;
      this.intentos = 0;
      this.reintentoRapidoGastado = false;
      this.opts.onEstado('en_vivo');
      this.iniciarHeartbeat();
      // Primer latido inmediato: la ventana de inercia del servidor (50 s)
      // arranca en el accept, no en el primer intervalo de 20 s.
      this.latir();
    };

    ws.onmessage = (e) => this.onMensaje(e);

    // onerror precede a onclose; dejamos que onclose maneje la reconexión.
    ws.onerror = () => {
      /* noop: onclose hace el trabajo */
    };

    ws.onclose = (e) => {
      this.detenerHeartbeat();
      if (this.cerradoPorCliente) return;
      // Rechazo de autorización: el servidor cierra con 4401 si llegó a
      // aceptar, y el navegador reporta un handshake fallido (sin `onopen`)
      // cuando lo rechaza antes. Ambos casos merecen un token nuevo.
      const quizasAuth = !abrio || e.code === CIERRE_AUTH;
      void this.programarReconexion(quizasAuth);
    };
  }

  private onMensaje(e: MessageEvent): void {
    let data: SalaEvento;
    try {
      data = JSON.parse(e.data as string) as SalaEvento;
    } catch {
      return;
    }
    // Todo mensaje con event_id avanza el puntero de replay.
    if (data.event_id) this.lastEventId = data.event_id;

    if (data.type === 'replay_fin') {
      if (data.last_event_id) this.lastEventId = data.last_event_id;
      this.opts.onReplayFin?.(data.last_event_id);
      return;
    }
    this.opts.onEvento(data);
  }

  private async programarReconexion(quizasAuth = false): Promise<void> {
    this.ws = null;
    this.intentos += 1;
    this.opts.onEstado(this.intentos >= UMBRAL_DEGRADADO ? 'degradado' : 'conectando');

    // Token vencido: renovarlo ANTES del próximo intento. Si el token nuevo es
    // distinto del que acaba de ser rechazado, reintentamos casi de inmediato
    // —el rechazo ya está explicado—. Si NO cambió (p. ej. no somos miembros
    // del proceso), seguimos con el backoff normal: refrescar no arreglaría
    // nada y un reintento rápido sería una tormenta.
    let tokenNuevo = false;
    if (quizasAuth && this.opts.refrescarToken && !this.reintentoRapidoGastado) {
      const ahora = Date.now();
      if (ahora - this.ultimoRefresh >= REFRESH_MIN_MS) {
        this.ultimoRefresh = ahora;
        try {
          if (await this.opts.refrescarToken()) {
            tokenNuevo = this.opts.getToken() !== this.tokenProbado;
            if (tokenNuevo) this.reintentoRapidoGastado = true;
          }
        } catch {
          /* sin red: el backoff normal sigue mandando */
        }
      }
    }
    if (this.cerradoPorCliente) return;

    // Rechazo de auth que ya sobrevivió a un token nuevo: no es la credencial.
    const authIrresoluble = quizasAuth && this.reintentoRapidoGastado && !tokenNuevo;
    const techo = authIrresoluble ? MAX_AUTH_MS : MAX_MS;

    // Full jitter: espera aleatoria entre 0 y el techo exponencial acotado.
    const cap = Math.min(techo, BASE_MS * 2 ** (this.intentos - 1));
    const delay = tokenNuevo ? REINTENTO_TOKEN_NUEVO_MS : Math.random() * cap;
    if (this.reconnTimer) window.clearTimeout(this.reconnTimer);
    this.reconnTimer = window.setTimeout(() => {
      if (!this.cerradoPorCliente) this.abrir();
    }, delay);
  }

  /**
   * El teléfono vuelve al frente. Mientras estuvo bloqueado el navegador
   * congela los timers: el keepalive no salió, el servidor cerró por inercia
   * (50 s) y el socket quedó muerto sin que nadie lo note. Al volver, latimos
   * de inmediato y, si el canal ya no está abierto, reconectamos sin esperar
   * el backoff.
   */
  private escucharVisibilidad(): void {
    if (typeof document === 'undefined' || this.onVisibilidad) return;
    this.onVisibilidad = () => {
      if (document.visibilityState !== 'visible' || this.cerradoPorCliente) return;
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.latir();
        return;
      }
      // Ni abierto ni conectando: reconectá ya (cancela la espera pendiente).
      if (this.ws?.readyState === WebSocket.CONNECTING) return;
      if (this.reconnTimer) {
        window.clearTimeout(this.reconnTimer);
        this.reconnTimer = null;
      }
      this.abrir();
    };
    document.addEventListener('visibilitychange', this.onVisibilidad);
  }

  private dejarDeEscucharVisibilidad(): void {
    if (this.onVisibilidad && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilidad);
    }
    this.onVisibilidad = null;
  }

  private latir(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify({ type: 'heartbeat' }));
    } catch {
      /* noop */
    }
  }

  private iniciarHeartbeat(): void {
    this.detenerHeartbeat();
    this.hbTimer = window.setInterval(() => this.latir(), HEARTBEAT_MS);
  }

  private detenerHeartbeat(): void {
    if (this.hbTimer) {
      window.clearInterval(this.hbTimer);
      this.hbTimer = null;
    }
  }

  private limpiarTimers(): void {
    this.detenerHeartbeat();
    if (this.reconnTimer) {
      window.clearTimeout(this.reconnTimer);
      this.reconnTimer = null;
    }
  }
}
